import React, { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Trash2, StickyNote } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNotes } from "./NotesContext";
import ProductPill from "./ProductPill";

export const NoteDialog = ({ open, onOpenChange, item, product, section = "" }) => {
  const { addNote, deleteNote, notes, identityFor } = useNotes();
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  if (!item) return null;
  const body = item.title || item.text || "";
  const sourceProduct = product || (item.products && item.products[0]) || null;
  const linkedProduct = identityFor(sourceProduct?.slug) || sourceProduct;
  const existing = notes.filter((n) => n.item_key === item.key);

  const save = async () => {
    const value = text.trim();
    if (!value) return;
    setSaving(true);
    try {
      await addNote({
        item_key: item.key,
        note: value,
        item_text: body,
        section,
        product_slug: linkedProduct?.slug || "",
        product_name: linkedProduct?.name || "",
      });
      setText("");
      toast.success("Note saved for the Master Planner");
    } catch (e) {
      toast.error("We couldn't save that note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteNote(id);
      toast.success("Note removed");
    } catch (e) {
      toast.error("We couldn't remove that note.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto product-identity-surface" style={{ "--product-swatch": linkedProduct?.hex }} data-testid="note-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Leave a note</DialogTitle>
          <DialogDescription>
            The Master Planner reads these and turns them into next steps: backlog for the team,
            a reminder for next time, or a change in what comes first.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-[hsl(var(--muted)/0.5)] p-3">
          {linkedProduct && (
            <div className="mb-2">
              <ProductPill product={linkedProduct} testId="note-dialog-product-tag" />
            </div>
          )}
          <p className="text-sm text-foreground leading-relaxed">{body}</p>
        </div>

        {existing.length > 0 && (
          <div className="space-y-2" data-testid="note-dialog-existing">
            <p className="eyebrow">Notes on this item</p>
            {existing.map((n) => (
              <div
                key={n.id}
                className="flex items-start justify-between gap-3 rounded-lg border p-2.5"
                data-testid="existing-note-row"
              >
                <div className="min-w-0">
                  <p className="text-sm text-foreground leading-relaxed">{n.note}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 h-8 w-8 p-0"
                  onClick={() => remove(n.id)}
                  data-testid="delete-note-button"
                  aria-label="Remove note"
                >
                  <Trash2 size={15} />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your note... e.g. 'remind me to raise this with the team' or 'do this first next time'."
            rows={3}
            data-testid="note-textarea"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") save();
            }}
          />
          <p className="text-[11px] text-muted-foreground">Tip: press Cmd/Ctrl + Enter to save.</p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="note-cancel-button">
            Close
          </Button>
          <Button onClick={save} disabled={saving || !text.trim()} data-testid="note-save-button" className="gap-1.5">
            <StickyNote size={15} />
            {saving ? "Saving..." : "Save note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoteDialog;
